import React, { useState, useEffect } from 'react';
import { Icons } from '../constants';
import { User } from '../types';
import { auth, db } from '../firebase';
import { 
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile 
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { beacon } from '../lib/beacon';
import { Logo, BrandName } from './Logo';

interface Props {
  onLogin: (user: User) => void;
}

// Self-service account types. Employee and admin are deliberately absent:
// only an existing admin can grant those from Users & Access.
const SIGNUP_TYPES = [
  { id: 'client',     icon: '🏡', label: 'Client / Customer', desc: 'Book services and track your property' },
  { id: 'contractor', icon: '🪓', label: 'Contractor',        desc: 'Crew lead or contract climber' },
  { id: 'friend',     icon: '🤝', label: 'Friend of GreenTree', desc: 'Community updates and referrals' },
  { id: 'applicant',  icon: '📋', label: 'Job Applicant',     desc: 'Apply to join the crew' },
] as const;
type SignupType = typeof SIGNUP_TYPES[number]['id'];

// Bootstrap accounts only. These are the two owner accounts; nobody else is
// promoted by having a particular word in their email address.
const OWNER_EMAILS = new Set([
  'mrgreen@greentreearboriculture.com',
  'brandtmgreen@gmail.com',
]);

const AuthView: React.FC<Props> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Form Fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState<SignupType>('client');
  const [resetSent, setResetSent] = useState(false);

  const profileForSignup = (
    emailStr: string,
    nameStr: string,
    uid: string,
    accountType: SignupType,
    addressStr = '',
  ): User => {
    const isContractor = accountType === 'contractor';
    const isApplicant = accountType === 'applicant';
    const profile: User = {
      id: uid,
      name: nameStr || 'Arbor Tree Fanatic',
      role: isContractor ? 'staff' : isApplicant ? 'applicant' : 'client',
      accountType,
      email: emailStr,
      address: addressStr,
    };
    // Firestore rejects undefined values; only write staffRole when it applies.
    if (isContractor) profile.staffRole = 'contractor';
    return profile;
  };

  const profileForOwner = (emailStr: string, nameStr: string, uid: string, addressStr = ''): User => ({
    id: uid,
    name: nameStr || 'GreenTree Owner',
    role: 'staff',
    staffRole: 'admin',
    accountType: 'admin',
    email: emailStr,
    address: addressStr,
  });

  const handleEmailLogin = async (firebaseUser: any) => {
    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      const emailStr = (firebaseUser.email || '').toLowerCase();
      let profile: User;
      if (userDocSnap.exists()) {
        const existing = userDocSnap.data() as User;
        if (!existing.role) {
          // Repair an incomplete legacy profile, but never grant staff from an email.
          const repaired = OWNER_EMAILS.has(emailStr)
            ? profileForOwner(emailStr, firebaseUser.displayName || existing.name || '', firebaseUser.uid, existing.address || '')
            : profileForSignup(emailStr, firebaseUser.displayName || existing.name || '', firebaseUser.uid, 'client', existing.address || '');
          profile = { ...existing, ...repaired };
          await setDoc(userDocRef, { ...profile, lastLogin: new Date().toISOString() }, { merge: true });
        } else {
          // Existing accounts keep the role an admin gave them. Login never
          // recomputes role/staffRole/accountType from the email address.
          const patch: Record<string, any> = { lastLogin: new Date().toISOString() };
          if (!existing.name && firebaseUser.displayName) patch.name = firebaseUser.displayName;
          profile = { ...existing, ...patch };
          await setDoc(userDocRef, patch, { merge: true });
        }
      } else {
        profile = OWNER_EMAILS.has(emailStr)
          ? profileForOwner(emailStr, firebaseUser.displayName || '', firebaseUser.uid, address)
          : profileForSignup(emailStr, firebaseUser.displayName || '', firebaseUser.uid, 'client', address);
        await setDoc(userDocRef, {
          ...profile,
          createdAt: new Date().toISOString()
        });
      }
      onLogin(profile);
    } catch (err: any) {
      console.error("Profile login error:", err);
      setError(err?.message || 'Failed to finish sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileLogin = handleEmailLogin;

  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          handleEmailLogin(result.user);
        }
      })
      .catch((error) => {
        console.error("Redirect Result error:", error);
      });
  }, [onLogin]);

  const handleGoogleLogin = async () => {
    setError('');
    if (loading) return;
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      try {
        // Bounded popup attempt: a COOP-severed popup can hang without resolving or
        // rejecting, leaving the user stuck. If it doesn't finish in 45s, hand off to
        // the redirect flow (which getRedirectResult now completes).
        const popupPromise = signInWithPopup(auth, provider);
        popupPromise.catch(() => { /* late rejections handled by the race */ });
        const result = await Promise.race([
          popupPromise,
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('popup-timeout')), 45000)),
        ]);
        if (result?.user) {
          await handleEmailLogin(result.user);
          return;
        }
      } catch (popupErr: any) {
        beacon('popup-fallback', popupErr);
        console.warn("Popup Google sign-in failed, falling back to redirect:", popupErr);
        await signInWithRedirect(auth, provider);
      }
    } catch (err: any) {
      beacon('google-login-error', err);
      console.error("Google Login error:", err);
      setError(err?.message || 'Failed to complete Google login');
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    const target = email.trim();
    if (!target) {
      setError('Enter your email address first, then tap "Forgot password?".');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, target, {
        url: window.location.origin,
      });
      setResetSent(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      // Do not reveal whether an account exists. Firebase's email-enumeration
      // protection already returns success for unknown addresses; handle the
      // legacy user-not-found response the same way.
      if (err?.code === 'auth/user-not-found') {
        setResetSent(true);
      } else if (err?.code === 'auth/invalid-email') {
        setError('Enter a valid email address.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many reset attempts. Please wait a few minutes and try again.');
      } else {
        setError('We could not send the reset email. Check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const normalizedEmail = email.trim();

    if (!normalizedEmail || !password) {
      setError('Please fill in both email and password.');
      setLoading(false);
      return;
    }

    if (!isLogin) {
      if (!fullName.trim()) {
        setError('Please enter your full name to register.');
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError('Choose a password with at least 8 characters.');
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError('The passwords do not match. Please retype them carefully.');
        setLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        const credential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
        await handleEmailLogin(credential.user);
      } else {
        const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        const user = credential.user;
        const cleanName = fullName.trim();

        await updateProfile(user, { displayName: cleanName });

        const profile = profileForSignup(normalizedEmail.toLowerCase(), cleanName, user.uid, selectedAccountType, address);

        await setDoc(doc(db, 'users', user.uid), {
          ...profile,
          createdAt: new Date().toISOString()
        });

        onLogin(profile);
      }
    } catch (err: any) {
      console.error("Email auth error:", err);
      let errMsg = 'Authentication failed. Please verify your credentials.';
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errMsg = 'That email and password do not match. If you have forgotten your password, use the "Forgot password?" link. If you have not created an account yet, switch to Create Account below.';
      } else if (err.code === 'auth/email-already-in-use') {
        errMsg = 'This email address is already registered. Please click "Sign In" to log in!';
      } else if (err.code === 'auth/weak-password') {
        errMsg = 'The password must be at least 8 characters.';
      } else if (err.code === 'auth/invalid-email') {
        errMsg = 'Please provide a valid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        errMsg = 'Too many attempts. Please wait a few minutes and try again.';
      } else if (err.code === 'auth/network-request-failed') {
        errMsg = 'Network error. Check your connection and try again.';
      } else if (err.code === 'auth/operation-not-allowed') {
        errMsg = 'Email/password sign-in is not enabled for this project. Contact an administrator.';
      } else if (err.code === 'auth/user-disabled') {
        errMsg = 'This account has been disabled. Contact an administrator.';
      } else if (err.message) {
        errMsg = err.message;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-palm rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold rounded-full blur-[100px]" />
      </div>

      <div className="max-w-5xl w-full bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col md:flex-row border border-stone-100 relative z-10 my-8">
        {/* Left Branding Panel */}
        <div className="md:w-5/12 bg-bark p-10 text-white flex flex-col justify-between relative overflow-hidden bg-cover bg-center" style={{ backgroundImage: "linear-gradient(to bottom, rgba(77, 40, 9, 0.8), rgba(77, 40, 9, 0.95)), url('/greentree-3d.jpg')" }}>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-8">
              <Logo className="w-14 h-14 animate-float" />
              <BrandName className="text-2xl" />
            </div>
            
            <h1 className="text-3xl font-black leading-tight mb-4 text-[#A8D672]">
              Sustain, Protect & Grow Columbus Canopy.
            </h1>
            <p className="text-[#D4AF37] font-bold text-lg tracking-tight mb-6">
              GreenTree Arboriculture Management
            </p>
            <p className="text-stone-300 leading-relaxed text-xs mb-6 font-medium">
              Join Mr. Green & the advanced aerial assessment team. Book drone mapping runs, consult canopy health reports, and manage local tree care bids.
            </p>
            
            <div className="bg-white/10 p-5 rounded-2xl border border-white/10 backdrop-blur-sm mb-6">
              <h3 className="text-gold font-black uppercase tracking-widest text-[10px] mb-3">Live Portal Capabilities</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-xs font-semibold text-stone-200">
                  <span className="w-5 h-5 rounded-full bg-gecko/20 text-[#A8D672] flex items-center justify-center mr-3 text-[10px] font-black">✓</span>
                  Real-time Firebase Syncing
                </li>
                <li className="flex items-center text-xs font-semibold text-stone-200">
                  <span className="w-5 h-5 rounded-full bg-gecko/20 text-[#A8D672] flex items-center justify-center mr-3 text-[10px] font-black">✓</span>
                  Instant Drone Flight Missions
                </li>
                <li className="flex items-center text-xs font-semibold text-stone-200">
                  <span className="w-5 h-5 rounded-full bg-gecko/20 text-[#A8D672] flex items-center justify-center mr-3 text-[10px] font-black">✓</span>
                  Google Workspace Direct Integration
                </li>
              </ul>
            </div>
          </div>
          
          <div className="relative z-10 flex items-center space-x-4 mt-6">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center border border-white/5">
               <span className="text-2xl">🐒</span>
            </div>
            <div>
              <p className="font-black text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Pay It Forward Foundation</p>
              <p className="text-[9px] text-[#A8D672] font-black uppercase">Karma Wheel Charities</p>
            </div>
          </div>

          <div className="absolute -bottom-16 -right-16 w-64 h-64 opacity-5 pointer-events-none">
             <div className="w-full h-full border-[15px] border-gold rounded-full flex items-center justify-center p-12">
                <Icons.Tree className="w-full h-full text-gold" />
             </div>
          </div>
        </div>

        {/* Right Auth Panel */}
        <div className="md:w-7/12 p-10 bg-white flex flex-col justify-center">
          <div className="max-w-md w-full mx-auto">

            <h2 className="text-2xl font-black text-stone-900 mb-1">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-stone-500 mb-5 text-xs font-semibold uppercase tracking-wider">
              {isLogin ? 'Sign in to access your secure portal' : 'Register to request flights & log bids'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div role="alert" aria-live="assertive" className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-xs font-bold animate-subtle-shake">
                  <p className="mb-1 text-center">⚠️ {error}</p>
                  {isLogin && error.includes('do not match') && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(false);
                        setError('');
                        setResetSent(false);
                        setConfirmPassword('');
                      }}
                      className="mt-2 w-full py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-xl font-black text-[10px] uppercase tracking-widest transition"
                    >
                      👉 Click Here to Switch to Registration
                    </button>
                  )}
                </div>
              )}

              {resetSent && (
                <div role="status" aria-live="polite" className="p-4 bg-green-50 border border-green-200 rounded-2xl text-green-800 text-xs font-bold">
                  <p className="font-black uppercase tracking-wider text-[10px] mb-1">Check your inbox</p>
                  <p className="leading-relaxed">
                    If an account exists for <span className="break-all">{email.trim()}</span>, a password
                    reset link is on its way. It can take a minute — check spam or promotions too.
                  </p>
                  <button
                    type="button"
                    onClick={() => setResetSent(false)}
                    className="mt-3 w-full py-2 bg-green-100 hover:bg-green-200 rounded-xl font-black text-[10px] uppercase tracking-widest transition"
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              {/* Account Registration Specific Fields */}
              {!isLogin && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fullname-input" className="block text-[10px] font-black uppercase tracking-widest text-stone-500 mb-1.5">
                      Full Name
                    </label>
                    <input 
                      id="fullname-input" name="fullname"
                      type="text" 
                      placeholder="e.g. Samuel Oakwood"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:bg-white transition"
                    />
                  </div>

                  <div>
                    <label htmlFor="street-address-input" className="block text-[10px] font-black uppercase tracking-widest text-stone-500 mb-1.5">
                      Street Address (Services Target)
                    </label>
                    <input 
                      id="street-address-input" name="street-address"
                      type="text" 
                      placeholder="e.g. 142 Peach Avenue, Columbus GA"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:bg-white transition"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-stone-500 mb-2">
                      I want to sign up as a:
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SIGNUP_TYPES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSelectedAccountType(t.id)}
                          className={`text-left p-3 rounded-xl border transition ${
                            selectedAccountType === t.id
                              ? 'bg-[#5FA136]/10 border-[#5FA136]'
                              : 'bg-stone-50 border-stone-200 hover:bg-stone-100'
                          }`}
                        >
                          <span className="text-lg" aria-hidden="true">{t.icon}</span>
                          <span className={`block text-xs font-black uppercase tracking-wider mt-1 ${
                            selectedAccountType === t.id ? 'text-[#376E20]' : 'text-stone-600'
                          }`}>
                            {t.label}
                          </span>
                          <span className="block text-[10px] text-stone-400 mt-0.5">{t.desc}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[9px] text-stone-400 font-bold uppercase mt-2.5 tracking-wider text-center">
                      Employee and admin access is granted by an existing admin from Users &amp; Access.
                    </p>
                  </div>
                </div>
              )}

              {/* Universal Auth Fields */}
              <div>
                <label htmlFor="email-input" className="block text-[10px] font-black uppercase tracking-widest text-stone-500 mb-1.5">
                  Email Address
                </label>
                <input 
                  id="email-input" name="email"
              autoComplete="username"
              type="email" 
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (resetSent) setResetSent(false);
                  }}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:bg-white transition"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password-input" className="block text-[10px] font-black uppercase tracking-widest text-stone-500">
                    Secure Password
                  </label>
                  {isLogin && !resetSent && (
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={loading}
                      className="text-[10px] font-black uppercase tracking-widest text-[#5FA136] hover:text-[#376E20] transition disabled:opacity-50"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input 
                  id="password-input" name="password"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              type="password" 
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:bg-white transition"
                  required
                  minLength={isLogin ? undefined : 8}
                  aria-describedby={!isLogin ? 'password-hint' : undefined}
                />
                {!isLogin && (
                  <p id="password-hint" className="mt-1 text-[10px] text-stone-400 font-semibold">
                    Use at least 8 characters. Longer is stronger.
                  </p>
                )}
              </div>

              {!isLogin && (
                <div>
                  <label htmlFor="confirm-password-input" className="block text-[10px] font-black uppercase tracking-widest text-stone-500 mb-1.5">
                    Verify Password
                  </label>
                  <input
                    id="confirm-password-input" name="confirm-password"
                    autoComplete="new-password"
                    type="password"
                    placeholder="Retype your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-xs font-bold text-stone-900 focus:outline-none focus:ring-2 focus:ring-gecko/40 focus:bg-white transition"
                    required
                    minLength={8}
                    aria-invalid={confirmPassword.length > 0 && password !== confirmPassword}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p role="alert" className="mt-1 text-[10px] font-bold text-red-600">
                      Passwords do not match yet.
                    </p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="mt-1 text-[10px] font-bold text-green-600">
                      Passwords match.
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <button 
                type="submit"
                disabled={loading}
                className="w-full mt-4 py-4 bg-palm text-white rounded-2xl font-black text-sm hover:bg-bark transition shadow-lg shadow-palm/20 transform active:scale-[0.98] uppercase tracking-widest flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                    <span>Authenticating...</span>
                  </span>
                ) : (
                  <span>{isLogin ? 'Sign In Securely' : 'Register Secure Profile'}</span>
                )}
              </button>
            </form>

            <div className="relative flex items-center py-4 my-2">
              <div className="flex-grow border-t border-stone-200"></div>
              <span className="flex-shrink-0 mx-4 text-stone-400 text-[10px] font-black uppercase tracking-widest">Or Connect via Google</span>
              <div className="flex-grow border-t border-stone-200"></div>
            </div>

            <button 
              type="button"
              disabled={loading}
              onClick={handleGoogleLogin}
              className="w-full py-4 bg-white text-stone-700 hover:bg-stone-50 transition rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center border border-stone-200 shadow-sm active:scale-[0.98]"
            >
              <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Fast Google Sign-In
            </button>

            {/* Display View Changer Toggle */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setResetSent(false);
                  setConfirmPassword('');
                }}
                className="text-xs font-black text-[#5FA136] hover:text-[#376E20] uppercase tracking-widest transition"
              >
                {isLogin 
                  ? '🆕 Need an account? Click here to Register!' 
                  : '🔑 Already registered? Click to Sign In'}
              </button>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-stone-100 text-center">
            <p className="text-[9px] text-[#376E20] font-black uppercase tracking-widest flex items-center justify-center space-x-1.5">
              <span>🌾 greenTree management platform v2.1.0</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthView;
